import styled from "@emotion/styled";
import { AnimatePresence, motion } from "framer-motion";
import React from "react";
import { Marker } from "react-map-gl/maplibre";
import { Visit } from "../../../system/AppState/AppVisitsState";
import { getStatusColor, getVisitStatus } from "../../AvailableVisitsAndTasksList";
import { getVisitPriceAndHourTexts } from "../main-utils";

export default function VisitMapMarker(props: {
  visit: Visit;
  onClick: React.MouseEventHandler<Element>;
  isActive: boolean;
  expanded: boolean;
}) {
  const { hoursText, priceText } = getVisitPriceAndHourTexts(props.visit);
  const { location } = props.visit.publicVisitBroadcastDetails;
  const statusColor = getStatusColor(getVisitStatus(props.visit));

  return (
    <Marker
      latitude={location.location.lat}
      longitude={location.location.lng}
      style={{
        zIndex: props.isActive ? 1 : undefined,
      }}
    >
      <DeadWhiteSpace onClick={props.onClick}>
        <Root data-active={props.isActive}>
          <Pill colorScheme={statusColor} />
          <Price>{priceText}</Price>

          <AnimatePresence initial={false}>
            {props.expanded && (
              <Hours
                initial={{ width: 0, opacity: 0 }}
                animate={{ width: "auto", opacity: 1 }}
                exit={{ width: 0, opacity: 0, marginLeft: `calc(var(--gap) * -1)` }}
              >
                {hoursText}
              </Hours>
            )}
          </AnimatePresence>
        </Root>
      </DeadWhiteSpace>
    </Marker>
  );
}

const DeadWhiteSpace = styled.div`
  padding: 12px;
`;

const Hours = styled(motion.div)`
  color: var(--tw-slate-300);
  font-weight: 400;
  white-space: nowrap;
`;

const Price = styled.span`
  font-weight: 700;
`;

const Pill = styled.div<{ colorScheme: "green" | "blue" | "slate" | "yellow" }>`
  --size: 6px;
  --bg-color: ${(props) => {
    switch (props.colorScheme) {
      case "green":
        return "var(--tw-green-500)";
      case "blue":
        return "var(--tw-blue-500)";
      case "yellow":
        return "var(--tw-amber-500)";
      case "slate":
        return "var(--tw-slate-300)";
    }
  }};

  width: var(--size);
  height: var(--size);
  border-radius: var(--size);
  background-color: var(--bg-color);
`;

const Root = styled(motion.div)<{ ["data-active"]: boolean }>`
  --bg-color: var(--tw-slate-900);
  --arrow-size: 4px;
  --ring-color: transparent;
  --gap: 8px;
  background-color: var(--bg-color);
  border-radius: 24px;
  color: var(--text-color, white);
  padding: 8px 12px;
  font-family: var(--font-family);
  font-size: 12px;
  line-height: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--gap);
  box-shadow: 0 0 0 4px var(--ring-color);
  transition: all 250ms ease;
  position: relative;

  &:hover {
    cursor: pointer;
  }

  &[data-active="true"] {
    --bg-color: var(--primary-color);
    --arrow-size: 8px;
    --ring-color: var(--tw-blue-200);
    font-size: 14px;
    padding: 12px 16px;
    transform: translateY(-6px);

    ${Pill} {
      --bg-color: white;
    }

    ${Hours} {
      color: var(--primary-soft-color);
    }
  }

  &:after {
    --real-arrow-size: calc(var(--arrow-size) + 1px);
    content: "";
    position: absolute;
    bottom: calc(var(--arrow-size) * -1 + 1px);
    left: 50%;
    transform: translateX(-50%);
    width: 0;
    height: 0;
    border-left: var(--real-arrow-size) solid transparent;
    border-right: var(--real-arrow-size) solid transparent;
    border-top: var(--real-arrow-size) solid var(--bg-color);
    transition: all 250ms ease;
  }
`;
